import type { Ref } from 'vue'
import { nextTick, onScopeDispose, watch } from 'vue'
import { useEventListener } from '@vueuse/core'

/** What a full-page overlay owes its user: scroll lock, Escape, focus return, an announced region. */
export function useOverlayChrome(options: {
  isOpen: Ref<boolean>
  onClose: () => void
  /** Focused on open; focus returns to whatever was active before. */
  region: Ref<HTMLElement | null>
}) {
  let previouslyFocused: HTMLElement | null = null
  let lockedScrollY = 0
  let scrollLocked = false

  const lockScroll = () => {
    lockedScrollY = window.scrollY
    document.body.style.top = `-${lockedScrollY}px`
    document.body.classList.add('overlay-open')
    scrollLocked = true
  }

  const releaseScroll = () => {
    if (!scrollLocked) return

    document.body.classList.remove('overlay-open')
    document.body.style.top = ''
    window.scrollTo({ top: lockedScrollY, behavior: 'instant' })
    scrollLocked = false
  }

  // The body lock outlives an owner unmounted mid-overlay unless released here.
  onScopeDispose(releaseScroll)

  useEventListener(document, 'keydown', (event: KeyboardEvent) => {
    if (!options.isOpen.value || event.key !== 'Escape') return

    // WdsModal and WdsDrawer close on Escape without stopping it; a DOM check until WDS offers a hook.
    if (document.querySelector('.wds-modal, .wds-drawer')) return

    options.onClose()
  })

  watch(
    options.isOpen,
    async (open, wasOpen) => {
      if (open === wasOpen) return

      if (open) {
        previouslyFocused = document.activeElement as HTMLElement | null
        lockScroll()
        await nextTick()
        options.region.value?.focus()
        return
      }

      // Immediate for deep links, but a closed overlay on mount is not a close.
      if (wasOpen === undefined) return

      releaseScroll()
      previouslyFocused?.focus()
      previouslyFocused = null
    },
    { immediate: true },
  )
}
